import React from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  hasLookupSuggestionChoices,
  type LookupSuggestion,
  type UserConfirmedLookups,
  type UpsertMappedPreviewResponse
} from '../../../types/salesforce';

/** Must match `IGNORE_VALUE` in `SyncConflictModal` (radio “skip” value). */
const LOOKUP_SKIP_VALUE = '__ignore__';

export interface SalesforceLookupSuggestionsSectionProps {
  lookupSuggestions: NonNullable<
    UpsertMappedPreviewResponse['lookup_suggestions']
  >;
  lookupSelections: UserConfirmedLookups;
  onLookupSelectionChange: (
    fieldApiName: string,
    sourceValue: string,
    salesforceId: string | null
  ) => void;
  getFieldLabel: (apiName: string) => string;
  disabled?: boolean;
}

export default function SalesforceLookupSuggestionsSection({
  lookupSuggestions,
  lookupSelections,
  onLookupSelectionChange,
  getFieldLabel,
  disabled = false
}: SalesforceLookupSuggestionsSectionProps) {
  const theme = useTheme();
  const primary =
    theme.customColors?.themePrimary || theme.palette.primary.main;
  const neutralPrimary =
    theme.customColors?.themeNeutralPrimaryColor ?? theme.palette.text.primary;
  const neutralSecondary =
    theme.customColors?.themeNeutralSecondaryColor ??
    theme.palette.text.secondary;
  const success =
    theme.customColors?.themeSuccess ?? theme.palette.success.main;
  const cardBorder = alpha(neutralSecondary, 0.3);
  const cardBg = alpha(primary, 0.02);
  const optionBorderSelected = primary;
  const optionBorderDefault = alpha(neutralSecondary, 0.3);

  if (!hasLookupSuggestionChoices(lookupSuggestions)) return null;

  const fieldEntries = Object.entries(lookupSuggestions).filter(
    ([, byValue]) =>
      !!byValue &&
      Object.values(byValue).some(
        (list) => Array.isArray(list) && list.length > 0
      )
  );

  if (fieldEntries.length === 0) return null;

  return (
    <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%' }}>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        gutterBottom
        sx={{ color: neutralPrimary }}
      >
        Lookup field matches
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Multiple Salesforce records matched a spreadsheet value. Choose which
        record to use for each lookup, or skip to leave the field unchanged on
        update.
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        {fieldEntries.map(([fieldApi, byValue]) => (
          <Box key={fieldApi} sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              {getFieldLabel(fieldApi)}
            </Typography>
            {Object.entries(byValue!).map(([sourceValue, suggestions]) => {
              if (!suggestions?.length) return null;
              const current = lookupSelections[fieldApi]?.[sourceValue];
              const exact = suggestions.find((s) => s.exact_match);
              const defaultId = exact?.id ?? suggestions[0]?.id;
              const selectedValue =
                current === null
                  ? LOOKUP_SKIP_VALUE
                  : (current ?? defaultId ?? '');

              return (
                <Box
                  key={`${fieldApi}:${sourceValue}`}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: cardBorder,
                    backgroundColor: cardBg,
                    minWidth: 0,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    gutterBottom
                    sx={{ color: neutralPrimary }}
                  >
                    Value: &quot;{sourceValue}&quot;
                  </Typography>
                  <FormControl
                    component="fieldset"
                    fullWidth
                    disabled={disabled}
                    sx={{ minWidth: 0, maxWidth: '100%' }}
                  >
                    <RadioGroup
                      value={selectedValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        onLookupSelectionChange(
                          fieldApi,
                          sourceValue,
                          v === LOOKUP_SKIP_VALUE ? null : v
                        );
                      }}
                    >
                      {suggestions.map((s: LookupSuggestion) => {
                        const isSelected = selectedValue === s.id;
                        return (
                          <FormControlLabel
                            key={s.id}
                            value={s.id}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={1}
                                sx={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  minWidth: 0,
                                  boxSizing: 'border-box',
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '2px solid',
                                  borderColor: isSelected
                                    ? optionBorderSelected
                                    : optionBorderDefault,
                                  cursor: disabled ? 'default' : 'pointer',
                                  overflow: 'hidden',
                                  '&:hover': {
                                    borderColor: disabled
                                      ? undefined
                                      : optionBorderSelected
                                  }
                                }}
                                onClick={() =>
                                  !disabled &&
                                  onLookupSelectionChange(
                                    fieldApi,
                                    sourceValue,
                                    s.id
                                  )
                                }
                              >
                                <Radio
                                  checked={isSelected}
                                  disabled={disabled}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    flexShrink: 0,
                                    color: neutralSecondary,
                                    '&.Mui-checked': { color: primary }
                                  }}
                                />
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden'
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                      color: neutralPrimary,
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere'
                                    }}
                                  >
                                    {s.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: neutralSecondary,
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere'
                                    }}
                                  >
                                    {s.id}
                                  </Typography>
                                </Box>
                                {s.exact_match && (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Exact match"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      flexShrink: 0,
                                      maxWidth: 'calc(100% - 8px)',
                                      backgroundColor: alpha(success, 0.1),
                                      color: success,
                                      border: `1px solid ${alpha(success, 0.3)}`,
                                      '& .MuiChip-icon': { color: success }
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            sx={{
                              m: 0,
                              mb: 1,
                              width: '100%',
                              maxWidth: '100%',
                              alignItems: 'flex-start',
                              '& .MuiFormControlLabel-label': {
                                maxWidth: '100%',
                                minWidth: 0
                              }
                            }}
                          />
                        );
                      })}
                      <FormControlLabel
                        value={LOOKUP_SKIP_VALUE}
                        control={<Radio sx={{ display: 'none' }} />}
                        label={
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              p: 1.5,
                              borderRadius: 1,
                              border: '2px solid',
                              borderColor:
                                selectedValue === LOOKUP_SKIP_VALUE
                                  ? optionBorderSelected
                                  : optionBorderDefault,
                              cursor: disabled ? 'default' : 'pointer',
                              overflow: 'hidden',
                              '&:hover': {
                                borderColor: disabled
                                  ? undefined
                                  : optionBorderSelected
                              }
                            }}
                            onClick={() =>
                              !disabled &&
                              onLookupSelectionChange(
                                fieldApi,
                                sourceValue,
                                null
                              )
                            }
                          >
                            <Radio
                              checked={selectedValue === LOOKUP_SKIP_VALUE}
                              disabled={disabled}
                              size="small"
                              sx={{
                                p: 0,
                                mr: 1,
                                color: neutralSecondary,
                                '&.Mui-checked': { color: primary }
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: neutralPrimary,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                minWidth: 0
                              }}
                            >
                              Skip – don&apos;t set this lookup
                            </Typography>
                          </Box>
                        }
                        sx={{
                          m: 0,
                          width: '100%',
                          maxWidth: '100%',
                          alignItems: 'flex-start',
                          '& .MuiFormControlLabel-label': {
                            maxWidth: '100%',
                            minWidth: 0
                          }
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
